/**
 * Spec Phase 6 Slice 6E — paragraph delete confirm.
 *
 * Per Phase 6 brief §6: "Delete → confirm dialog 'Delete paragraph
 * 0009? This cannot be undone.' → DELETE."
 *
 * Lightweight portal-free modal — same pattern as
 * OverwriteConfirmDialog from Slice 5D.
 */
import React from 'react';
import { Loader2 } from 'lucide-react';

interface Props {
  paragraphNumber: string;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ParagraphDeleteConfirm({
  paragraphNumber,
  isPending,
  onConfirm,
  onCancel,
}: Props): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm delete paragraph"
      data-testid={`spec-paragraph-delete-confirm-${paragraphNumber}`}
    >
      <div className="w-full max-w-md rounded-sm border border-zinc-200 bg-white p-6 shadow-xl">
        <h2 className="mb-2 text-base font-semibold text-zinc-900">
          Delete paragraph {paragraphNumber}?
        </h2>
        <p className="mb-6 text-sm text-zinc-600">
          This cannot be undone from the editor. The paragraph
          number {paragraphNumber} won&rsquo;t be reused — surviving
          paragraphs keep their numbers (EP convention; gaps are
          accepted).
          {' '}Audit history is preserved in the diagnostic panel.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="inline-flex h-9 items-center rounded-sm border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            onClick={onCancel}
            disabled={isPending}
            data-testid={`spec-paragraph-delete-cancel-${paragraphNumber}`}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex h-9 items-center gap-1 rounded-sm bg-red-600 px-3 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            onClick={onConfirm}
            disabled={isPending}
            data-testid={`spec-paragraph-delete-confirm-btn-${paragraphNumber}`}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
